import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/images/xtreme.png';
import { MotionButton } from '../common/MotionButton';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: linear-gradient(145deg, rgba(44, 44, 44, 0.98) 0%, rgba(26, 26, 26, 0.98) 100%);
  backdrop-filter: blur(10px);
  z-index: 1000;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1) inset;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(70% 50% at 50% 0%, rgba(255, 107, 0, 0.05) 0%, transparent 100%),
      linear-gradient(90deg, transparent 0%, rgba(255, 107, 0, 0.02) 50%, transparent 100%);
    pointer-events: none;
  }
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  perspective: 1000px;
`;

const LogoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Tagline = styled(motion.span)`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  white-space: nowrap;
  position: relative;
  padding-left: 1.5rem;
  text-transform: uppercase;

  &::before {
    content: '★';
    position: absolute;
    left: 0;
    color: #FF6B00;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { 
      opacity: 0.5; 
      transform: scale(1) rotate(0deg); 
    }
    50% { 
      opacity: 1; 
      transform: scale(1.2) rotate(15deg); 
    }
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const LogoImage = styled.img`
  height: 150px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #FFFFFF;
  text-decoration: none;
  font-weight: 600;
  position: relative;
  padding: 0.5rem 0;
  font-size: 1.1rem;
  letter-spacing: -0.01em;
  transition: color 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: -0.5rem;
    background: radial-gradient(circle at center, rgba(255, 140, 0, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
    border-radius: 8px;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 2px;
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: scaleX(0);
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    transform-origin: left;
  }

  &:hover {
    color: #FFA500;
    &::before {
      opacity: 1;
    }
  }

  &:hover::after,
  &.active::after {
    transform: scaleX(1);
  }
`;

const AuthButton = styled(MotionButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 107, 0, 0.15);

  &:hover {
    box-shadow: 0 8px 25px rgba(255, 107, 0, 0.25);
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: rgba(255, 140, 0, 0.1);
  border: 1px solid rgba(255, 140, 0, 0.2);
  color: #FFFFFF;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    background: rgba(255, 140, 0, 0.15);
    border-color: rgba(255, 140, 0, 0.3);
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: linear-gradient(145deg, rgba(44, 44, 44, 0.98) 0%, rgba(26, 26, 26, 0.98) 100%);
    padding: 1.5rem;
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 140, 0, 0.2);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }
`;

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.1
      }
    }
  };

  const handleQuoteClick = () => {
    navigate('/calculator');
  };

  return (
    <Nav>
      <NavContainer>
        <LogoContainer to="/">
          <LogoImage
            src={logo}
            alt="Paisalocal"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          />
          <LogoGroup>
            <Tagline
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: 0.2, 
                duration: 0.4,
                ease: [0.16, 1, 0.3, 1] 
              }}
            >
              Quality That Speaks, Service That Delivers
            </Tagline>
          </LogoGroup>
        </LogoContainer>

        <NavLinks>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
            >
              {item.label}
            </NavLink>
          ))}
          <AuthButton 
            onClick={handleQuoteClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Free Quote
          </AuthButton>
        </NavLinks>

        <MobileMenuButton
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          whileTap={{ scale: 0.95 }}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>

        <AnimatePresence>
          {mobileMenuOpen && (
            <MobileMenu
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={location.pathname === item.path ? 'active' : ''}
                >
                  {item.label}
                </NavLink>
              ))}
              <AuthButton 
                onClick={handleQuoteClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Free Quote
              </AuthButton>
            </MobileMenu>
          )}
        </AnimatePresence>
      </NavContainer>
    </Nav>
  );
};

export default NavBar;